import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  groupBy,
  map,
  mergeMap,
  switchMap,
  take,
  throttleTime,
} from 'rxjs/operators';

import { createEffectFactory } from '@core/ngrx-legacy';
import type { PaginatedPipelineParams } from '@core/stages-types';

import { PipelineStagesHttpService } from './pipeline-stages-http.service';
import type { StagePagination } from './pipeline-stages.store';
import { PipelineStagesActions, PipelineStagesSelectors } from './pipeline-stages.store';

@Injectable({
  providedIn: 'root',
})
export class PipelineStagesEffects {
  private readonly http = inject(PipelineStagesHttpService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  public effects = createEffectFactory(PipelineStagesActions, this.actions$);

  public fetch$ = this.effects(PipelineStagesActions.fetchOne, ({ payload: { id, params } }) =>
    this.http.fetchOne$(id, params).pipe(
      map(response => ({
        data: {
          stages: response.stages,
          id: response.id,
          pagination: {},
        },
      })),
    ),
  );

  public updateStagePlacements$ = this.effects(
    PipelineStagesActions.updateStagePlacements,
    ({ payload: { offerSlug, params } }) =>
      this.http.fetchStagePlacements$(offerSlug, params).pipe(
        map(placements =>
          PipelineStagesActions.updateStagePlacementsSuccess({
            payload: { placements, offerSlug, stageId: params.stage_id },
          }),
        ),
        catchError((error: HttpErrorResponse) =>
          of(
            PipelineStagesActions.updateStagePlacementsFailed({
              payload: { error, offerSlug: offerSlug },
            }),
          ),
        ),
      ),
  );

  public fetchStagePlacementsPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PipelineStagesActions.fetchStagePlacementsPaginated),
      groupBy(({ payload }) => `${payload.offerSlug}:${payload.params.stage_id}`),
      mergeMap(group =>
        group.pipe(
          switchMap(({ payload: { offerSlug, params } }) =>
            this.http.fetchStagePlacementsPaginated$(offerSlug, params).pipe(
              map(({ placements, meta }) =>
                PipelineStagesActions.fetchStagePlacementsPaginatedSuccess({
                  payload: {
                    placements,
                    meta,
                    offerSlug,
                    stageId: params.stage_id,
                    mode: params.page === 1 ? 'replace' : 'append',
                  },
                }),
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  PipelineStagesActions.fetchStagePlacementsPaginatedFailed({
                    payload: { error, offerSlug, stageId: params.stage_id },
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  public refreshLoadedStagePlacements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PipelineStagesActions.refreshLoadedStagePlacements),
      groupBy(({ payload }) => `${payload.offerSlug}:${payload.stageId}`),
      mergeMap(group =>
        group.pipe(
          throttleTime(500),
          switchMap(({ payload: { offerSlug, stageId, params } }) =>
            this.store.select(PipelineStagesSelectors.pagination(offerSlug)).pipe(
              take(1),
              map(paginationMap => paginationMap[stageId]),
              filter((pagination): pagination is StagePagination => !!pagination),
              switchMap(pagination => {
                const requestLimit = Math.min(pagination.page * pagination.limit, 1000);
                const normalizedPage = Math.floor(requestLimit / pagination.limit);
                const fetchParams: PaginatedPipelineParams = {
                  ...params,
                  stage_id: stageId,
                  page: 1,
                  limit: requestLimit,
                };
                return this.http.fetchStagePlacementsPaginated$(offerSlug, fetchParams).pipe(
                  map(({ placements, meta }) =>
                    PipelineStagesActions.fetchStagePlacementsPaginatedSuccess({
                      payload: {
                        placements,
                        meta: {
                          page: normalizedPage,
                          limit: pagination.limit,
                          totalCount: meta.totalCount,
                        },
                        offerSlug,
                        stageId,
                        mode: 'replace',
                      },
                    }),
                  ),
                  catchError((error: HttpErrorResponse) =>
                    of(
                      PipelineStagesActions.fetchStagePlacementsPaginatedFailed({
                        payload: { error, offerSlug, stageId },
                      }),
                    ),
                  ),
                );
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updatePlacement$ = this.effects(PipelineStagesActions.updatePlacement, ({ payload }) =>
    this.http.fetchPlacement(payload.offerId, payload.candidateId, payload.params).pipe(
      map(data =>
        PipelineStagesActions.updatePlacementSuccess({
          payload: { data, offerSlug: payload.offerSlug },
        }),
      ),
      catchError((error: HttpErrorResponse) =>
        of(
          PipelineStagesActions.updatePlacementFailed({
            payload: { error, offerSlug: payload.offerSlug },
          }),
        ),
      ),
    ),
  );
}
